import type { RepairOrderStatus } from "@autoflow/types";
import { http } from "../../shared/api/http";
import type {
  ArrivalBoardResponse, ArrivalSearchResponse, RepairOrderListResponse,
  BusinessOperationsResponse, RepairWorkspaceData, ServiceCatalogMatch, WorkflowData, WorkshopQueueResponse
} from "./repair-order-types";

export type RepairOrderInput = {
  customerId: string;
  vehicleId: string;
  promisedAt?: string;
  mileageIn?: number;
  customerConcern?: string;
  internalNotes?: string;
};

export type FastCheckInInput = {
  customerId?: string;
  vehicleId?: string;
  customer?: {
    salutation?: "MR" | "MS";
    firstName: string;
    lastName: string;
    phone: string;
    alternatePhone?: string;
    email?: string;
  };
  vehicle?: { vin: string; year: number; make: string; model: string; licensePlate?: string };
  customerConcern: string;
  mileageIn?: number;
  promisedAt?: string;
};

export const repairOrdersApi = {
  async list(search = "", status?: RepairOrderStatus, page = 1, options?: {
    pageSize?: number;
    openedFrom?: string;
    openedTo?: string;
  }) {
    return (await http.get<RepairOrderListResponse>("/v1/repair-orders", {
      params: {
        search: search || undefined,
        status,
        page,
        pageSize: options?.pageSize ?? 20,
        openedFrom: options?.openedFrom,
        openedTo: options?.openedTo
      }
    })).data;
  },
  async workspace(id: string) {
    return (await http.get<RepairWorkspaceData>(`/v1/repair-orders/${id}/workspace`)).data;
  },
  async arrivalBoard() {
    return (await http.get<ArrivalBoardResponse>("/v1/repair-orders/arrival-board")).data;
  },
  async arrivalSearch(query: string) {
    return (await http.get<ArrivalSearchResponse>("/v1/repair-orders/arrival-search", {
      params: { q: query }
    })).data;
  },
  async create(input: RepairOrderInput) {
    return (await http.post<{ id: string }>("/v1/repair-orders", input)).data;
  },
  async fastCheckIn(input: FastCheckInInput) {
    return (await http.post<{
      id: string;
      roNumber: string;
      status: string;
      attention: { workspacePath: string; nextAction: string; state: string };
    }>("/v1/repair-orders/fast-check-in", input)).data;
  },
  async update(id: string, input: Partial<RepairOrderInput> & { mileageOut?: number }) {
    return (await http.patch(`/v1/repair-orders/${id}`, input)).data;
  },
  async changeStatus(id: string, status: RepairOrderStatus, reason?: string) {
    return (await http.post(`/v1/repair-orders/${id}/status`, { status, reason })).data;
  },
  async workflow(id: string) {
    return (await http.get<WorkflowData>(`/v1/repair-orders/${id}/workflow`)).data;
  },
  async businessOperations(id: string) {
    return (await http.get<BusinessOperationsResponse>(`/v1/repair-orders/${id}/business`)).data;
  },
  async recordCustomerCommunication(id: string, input: {
    channel: "SMS" | "EMAIL" | "PHONE" | "IN_PERSON";
    direction?: "OUTBOUND" | "INBOUND";
    purpose: "ESTIMATE" | "APPROVAL" | "READY_FOR_PICKUP" | "INVOICE" | "PAYMENT_RECEIPT" | "DELIVERY" | "FOLLOW_UP" | "WARRANTY";
    recipient?: string;
    message: string;
  }) {
    return (await http.post(`/v1/repair-orders/${id}/business/communications`, input)).data;
  },
  async deliverVehicle(id: string, input: {
    recipientName: string;
    mileageOut?: number;
    customerNotifiedAt?: string;
    notes?: string;
  }) {
    return (await http.post(`/v1/repair-orders/${id}/business/delivery`, input)).data;
  },
  async workshopQueue() {
    return (await http.get<WorkshopQueueResponse>("/v1/workshop/queue")).data;
  },
  async assignServiceTechnician(id: string, serviceId: string, technicianId: string) {
    return (await http.patch(`/v1/workshop/repair-orders/${id}/services/${serviceId}/technician`, {
      technicianId
    })).data;
  },
  async startServiceWork(id: string, serviceId: string) {
    return (await http.post(`/v1/workshop/repair-orders/${id}/services/${serviceId}/start`)).data;
  },
  async pauseServiceWork(id: string, serviceId: string) {
    return (await http.post(`/v1/workshop/repair-orders/${id}/services/${serviceId}/pause`)).data;
  },
  async resumeServiceWork(id: string, serviceId: string) {
    return (await http.post(`/v1/workshop/repair-orders/${id}/services/${serviceId}/resume`)).data;
  },
  async completeServiceWork(id: string, serviceId: string) {
    return (await http.post(`/v1/workshop/repair-orders/${id}/services/${serviceId}/complete`)).data;
  },
  async issueServicePart(id: string, serviceId: string, servicePartId: string, quantity: number) {
    return (await http.post(
      `/v1/workshop/repair-orders/${id}/services/${serviceId}/parts/${servicePartId}/issue`,
      { quantity }
    )).data;
  },
  async serviceQc(id: string, serviceId: string, passed: boolean, note?: string) {
    return (await http.post(
      `/v1/workshop/repair-orders/${id}/services/${serviceId}/qc/${passed ? "pass" : "fail"}`,
      { note }
    )).data;
  },
  async searchServiceCatalog(id: string, query = "") {
    return (await http.get<ServiceCatalogMatch[]>(`/v1/repair-orders/${id}/services/catalog`, {
      params: { q: query || undefined }
    })).data;
  },
  async addService(id: string, input: { serviceCatalogId: string; laborRate?: number; notes?: string }) {
    return (await http.post(`/v1/repair-orders/${id}/services`, input)).data;
  },
  async removeService(id: string, serviceId: string) {
    return (await http.delete(`/v1/repair-orders/${id}/services/${serviceId}`)).data;
  },
  async overrideServiceLabor(id: string, serviceId: string, input: {
    finalLaborTime: number;
    laborRate?: number;
    overrideReason: string;
  }) {
    return (await http.patch(`/v1/repair-orders/${id}/services/${serviceId}/labor`, input)).data;
  },
  async addServicePart(id: string, serviceId: string, input: {
    partSource: "OEM_LIBRARY" | "INVENTORY" | "MANUAL" | "CUSTOMER_SUPPLIED";
    oemPartLibraryId?: string;
    inventoryItemId?: string;
    partNumber: string;
    description: string;
    qty: number;
    unitCost?: number;
    unitPrice: number;
    supplier?: string;
    warrantyMonth?: number;
    notes?: string;
  }) {
    return (await http.post(`/v1/repair-orders/${id}/services/${serviceId}/parts`, input)).data;
  },
  async addServiceEvidence(id: string, serviceId: string, input: {
    type: "PHOTO" | "VIDEO" | "MEASUREMENT" | "OBD" | "DOCUMENT" | "AUDIO" | "SIGNATURE" | "OEM_REFERENCE";
    summary?: string;
    value?: string;
    unit?: string;
    customerVisible?: boolean;
    file?: File;
  }) {
    const form = new FormData();
    form.append("type", input.type);
    if (input.summary) form.append("summary", input.summary);
    if (input.value) form.append("value", input.value);
    if (input.unit) form.append("unit", input.unit);
    form.append("customerVisible", String(input.customerVisible ?? true));
    if (input.file) form.append("file", input.file);
    return (await http.post(`/v1/repair-orders/${id}/services/${serviceId}/evidence`, form, {
      headers: { "Content-Type": "multipart/form-data" }
    })).data;
  },
  async updateWorkflowContext(id: string, input: Record<string, unknown>) {
    return (await http.patch(`/v1/repair-orders/${id}/workflow/context`, input)).data;
  },
  async checkIn(id: string) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/check-in`)).data;
  },
  async addInspectionItem(id: string, input: { name: string; condition: string; note?: string }) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/inspection/items`, input)).data;
  },
  async removeInspectionItem(id: string, itemId: string) {
    await http.delete(`/v1/repair-orders/${id}/workflow/inspection/items/${itemId}`);
  },
  async completeInspection(id: string, summary?: string) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/inspection/complete`, { summary })).data;
  },
  async addEstimateLine(id: string, input: Record<string, unknown>) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/estimate/lines`, input)).data;
  },
  async removeEstimateLine(id: string, lineId: string) {
    await http.delete(`/v1/repair-orders/${id}/workflow/estimate/lines/${lineId}`);
  },
  async updateEstimateAdjustments(id: string, input: {
    shopSupplyAmount: number;
    discountAmount: number;
  }) {
    return (await http.patch(`/v1/repair-orders/${id}/workflow/estimate/adjustments`, input)).data;
  },
  async submitEstimate(id: string, taxRate: number, input?: {
    shopSupplyAmount?: number;
    discountAmount?: number;
  }) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/estimate/submit`, {
      taxRate,
      shopSupplyAmount: input?.shopSupplyAmount ?? 0,
      discountAmount: input?.discountAmount ?? 0
    })).data;
  },
  async approveEstimate(id: string, input: {
    approvedByName: string;
    approvalMethod: "TEXT" | "IMAGE" | "PHONE_RECORDING" | "SIGNED_ESTIMATE";
    approvalEvidence?: string;
    note?: string;
  }) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/estimate/approve`, input)).data;
  },
  async approveSupplement(id: string, input: {
    approvedByName: string;
    approvalMethod: "TEXT" | "IMAGE" | "PHONE_RECORDING" | "SIGNED_ESTIMATE";
    approvalEvidence?: string;
    note?: string;
  }) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/supplement/approve`, input)).data;
  },
  async uploadEstimateAttachment(id: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    return (await http.post(`/v1/repair-orders/${id}/workflow/estimate/attachments`, form, {
      headers: { "Content-Type": "multipart/form-data" }
    })).data;
  },
  async startWork(id: string) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/start-work`)).data;
  },
  async addWorkshopLabor(id: string, input: {
    description: string; hours: number; rate: number; technicianId?: string;
  }) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/workshop/labor`, input)).data;
  },
  async addWorkshopMaterial(id: string, input: {
    partId: string; quantity: number; supplier?: string; technicianId?: string;
  }) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/workshop/materials`, input)).data;
  },
  async addCustomWorkshopMaterial(id: string, input: {
    sku?: string; name: string; supplier?: string; quantity: number; unitPrice: number; notes?: string; technicianId?: string;
  }) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/workshop/custom-materials`, input)).data;
  },
  async addCustomerSuppliedPart(id: string, input: {
    name: string; quantity: number; notes?: string; technicianId?: string;
  }) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/workshop/customer-supplied-parts`, input)).data;
  },
  async startLabor(id: string, laborId: string) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/labor/${laborId}/start`)).data;
  },
  async completeLabor(id: string, laborId: string) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/labor/${laborId}/complete`)).data;
  },
  async issuePart(id: string, repairOrderPartId: string, quantity: number) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/parts/${repairOrderPartId}/issue`, { quantity })).data;
  },
  async qualityCheck(id: string) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/quality-check`)).data;
  },
  async readyDelivery(id: string) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/ready-delivery`)).data;
  },
  async recordPayment(id: string, input: { amount: number; method: string; reference?: string }) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/payments`, input)).data;
  },
  async close(id: string) {
    return (await http.post(`/v1/repair-orders/${id}/workflow/close`)).data;
  },
  async logDocumentPrint(id: string, documentType: "ESTIMATE" | "WORK_ORDER" | "INVOICE") {
    return (await http.post(`/v1/repair-orders/${id}/workflow/documents/print`, { documentType })).data;
  },
  async uploadRepairPhoto(id: string, file: File, caption?: string) {
    const form = new FormData();
    form.append("file", file);
    if (caption) form.append("caption", caption);
    return (await http.post(`/v1/repair-orders/${id}/workflow/photos`, form, {
      headers: { "Content-Type": "multipart/form-data" }
    })).data;
  }
};
